import React, { useRef, useState } from 'react';
import {
  type SmartZoneConfig,
  type SmartZoneRecordConfig,
} from '../../objects/SmartZoneBehaviors';
import {
  type RecordTypeData,
  type SmartZoneAttributes,
  type SmartZoneField,
  type SmartZoneFormData,
} from '../../shared-types';
import SelectField from '../common/SelectField';
import styles from './smart-zones.module.css';

interface SmartZoneEditorProps {
  smartZoneFormData: SmartZoneFormData;
  config: SmartZoneConfig;
  updateConfig: (
    updater: (current: SmartZoneConfig) => SmartZoneConfig
  ) => void;
}

interface FieldComponentProps {
  field: SmartZoneField;
  value: any;
  onChange: (value: any) => void;
  attributes: SmartZoneAttributes;
}

// Field component to render different field types
const FieldComponent: React.FC<FieldComponentProps> = ({
  field,
  value,
  onChange,
  attributes,
}) => {
  const key =
    ('getKey' in field ? field.getKey?.(attributes) : undefined) ??
    'defaultKey';

  const valueRef = useRef(value);
  valueRef.current = value;

  switch (field.fieldType) {
    case 'text':
      return (
        <input
          key={key}
          type='text'
          className={styles.fieldInput}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={`Enter ${field.description.toLowerCase()}`}
        />
      );
    case 'select':
    case 'multi-select': {
      const fetchProps =
        'fetchUrl' in field
          ? {
              fetchUrl: field.fetchUrl,
              resolveOptions: field.resolveOptions,
            }
          : {
              getOptions: async () => {
                const res = await field.fetch(attributes);
                const options = res.map(opt => ({ ...opt, item: opt.value }));

                // if the current value is not in the options, clear it
                if (!options.some(opt => opt.value === valueRef.current)) {
                  onChange(undefined);
                }

                return {
                  options,
                  hasMore: false,
                };
              },
            };
      const commonProps = {
        ...fetchProps,
        initialValue: value,
        key,
        showClearAll: true,
      };
      if (field.fieldType === 'multi-select') {
        return (
          <SelectField
            {...commonProps}
            setValue={val => {
              const selected = Array.from(val.values()).map(v => v.value);
              onChange(selected.length ? selected : undefined);
            }}
            multiple={true}
            branchesSelectable={field.branchesSelectable}
          />
        );
      } else {
        return (
          <SelectField
            {...commonProps}
            setValue={val => {
              onChange(val?.value);
            }}
            type='toggleSingle'
          />
        );
      }
    }
    default: {
      const exhaustiveCheck: never = field;
      throw new Error(`Unsupported field type: ${exhaustiveCheck}`);
    }
  }
};

interface RecordTypeSectionProps {
  recordTypeData: RecordTypeData;
  attributes: SmartZoneAttributes;
  onUpdate: (attributes: Partial<SmartZoneAttributes>) => void;
  onRemove: (() => void) | undefined;
}

// Component for a single record type section
const RecordTypeSection: React.FC<RecordTypeSectionProps> = ({
  recordTypeData,
  attributes,
  onUpdate,
  onRemove,
}) => {
  return (
    <div className={styles.recordTypeSection}>
      <div className={styles.recordTypeHeader}>
        <h3 className={styles.recordTypeName}>{recordTypeData.name}</h3>

        {onRemove && (
          <aha-button
            kind='icon'
            onClick={onRemove}
            aria-label='Remove record type'
          >
            <i className='fa-regular fa-close'></i>
          </aha-button>
        )}
      </div>

      {recordTypeData.fields.map(field => (
        <div key={field.name} className={styles.fieldRow}>
          <label className={styles.fieldLabel}>{field.description}</label>
          <div className={styles.fieldInputWrapper}>
            <FieldComponent
              attributes={attributes}
              field={field}
              value={attributes[field.name]}
              onChange={value => {
                onUpdate({
                  [field.name]: value,
                });
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export const SmartZoneEditor: React.FC<SmartZoneEditorProps> = ({
  smartZoneFormData,
  config,
  updateConfig,
}) => {
  const configurations = config?.configurations ?? {};
  const singleRecordType = smartZoneFormData.length === 1;
  const [showRecordTypeDropdown, setShowRecordTypeDropdown] =
    useState<boolean>(false);

  // record types that haven't been added yet
  const availableRecordTypes = smartZoneFormData.filter(
    data => !configurations[data.recordType]?.enabled
  );

  const updateRecordType = (
    recordType: string,
    updater: (current: SmartZoneRecordConfig) => SmartZoneRecordConfig
  ) =>
    updateConfig(current => ({
      ...current,
      configurations: {
        ...current.configurations,
        [recordType]: updater(current.configurations?.[recordType] ?? {}),
      },
    }));

  const handleUpdateAttributes = (
    recordType: string,
    attributes: SmartZoneAttributes
  ) =>
    updateRecordType(recordType, current => {
      return {
        ...current,
        attributes: {
          ...current.attributes,
          ...attributes,
        },
      };
    });

  const handleRemoveRecordType = (recordType: string) =>
    updateRecordType(recordType, current => {
      return {
        ...current,
        enabled: false,
      };
    });

  // Handle selecting a record type
  const handleRecordTypeChange = (recordType: string) => {
    if (!recordType) return;

    updateRecordType(recordType, current => {
      return {
        ...current,
        enabled: true,
      };
    });

    // Hide dropdown after selection
    setShowRecordTypeDropdown(false);
  };

  // Find the record type data for a given normalized record type
  const findRecordTypeData = (recordType: string) =>
    smartZoneFormData.find(val => val.recordType === recordType);

  const enabledConfigs = Object.entries(configurations).filter(
    ([, conf]) => conf.enabled
  );
  const configEmpty = !enabledConfigs.length;
  const recordTypesAvailable = availableRecordTypes.length > 0;

  return (
    <div className={styles.container}>
      {enabledConfigs.map(([recordType, { attributes }]) => {
        const recordTypeData = findRecordTypeData(recordType);
        if (!recordTypeData) return null;

        return (
          <RecordTypeSection
            key={recordType}
            recordTypeData={recordTypeData}
            attributes={attributes ?? {}}
            onUpdate={newAttributes =>
              handleUpdateAttributes(recordType, newAttributes)
            }
            onRemove={
              !singleRecordType
                ? () => handleRemoveRecordType(recordType)
                : undefined
            }
          />
        );
      })}

      {(configEmpty || showRecordTypeDropdown) && recordTypesAvailable ? (
        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel}>
            <strong>Record type</strong>
          </label>
          <div className={styles.fieldInputWrapper}>
            <SelectField
              initialValue={undefined}
              setValue={val => {
                handleRecordTypeChange(val.value);
                setShowRecordTypeDropdown(false);
              }}
              getOptions={async () => ({
                options: availableRecordTypes.map(data => ({
                  text: data.name,
                  value: data.recordType,
                  item: data,
                })),
              })}
            />
          </div>
        </div>
      ) : (
        recordTypesAvailable && (
          <div className={styles.addRecordTypeWrapper}>
            <aha-button
              kind='link'
              onClick={() => setShowRecordTypeDropdown(true)}
            >
              <i className='fa-light fa-circle-plus' /> Add record type
            </aha-button>
          </div>
        )
      )}
    </div>
  );
};
